import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, phpFmt } from "../lib/api";
import { toast, Toaster } from "sonner";
import { MapPin, Check, ArrowLeft, Shield, Mail, Phone, MessageCircle, User2, X } from "lucide-react";
import { formatApiError } from "../lib/auth";

const FWG_WHATSAPP_DISPLAY = "+63 917 534 3221";
const FWG_WHATSAPP_LINK = "https://wa.me/639175343221";

function InquiryModal({ open, onClose, centerId, centerName }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    if (!open) return null;

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.message.trim()) {
            toast.error("Please fill in all fields");
            return;
        }
        setBusy(true);
        try {
            await api.post("/inquiries", { center_id: centerId, ...form });
            setSent(true);
            toast.success("Inquiry sent — FWG will reach out shortly.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not send. Try WhatsApp instead.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-zinc-950/60" onClick={onClose} />
            <div className="relative bg-white border border-zinc-200 w-full max-w-md shadow-xl">
                <div className="flex items-center justify-between border-b border-zinc-200 p-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-label text-zinc-500">Inquire via FWG</div>
                        <div className="font-display text-lg font-semibold mt-0.5">{centerName}</div>
                    </div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" aria-label="Close">
                        <X className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                </div>

                {sent ? (
                    <div className="p-6 text-center">
                        <div className="mx-auto h-10 w-10 rounded-full bg-green-100 grid place-items-center">
                            <Check className="h-5 w-5 text-green-700" strokeWidth={1.5} />
                        </div>
                        <div className="mt-3 font-display text-lg font-semibold">Inquiry received</div>
                        <p className="mt-2 text-sm text-zinc-600">FWG will review and reach out to you shortly. You can also reach us faster on WhatsApp.</p>
                        <a
                            href={FWG_WHATSAPP_LINK}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="inquiry-success-whatsapp"
                            className="mt-4 inline-flex items-center gap-1.5 bg-[#25D366] text-white px-4 py-2 text-sm font-medium"
                        >
                            <MessageCircle className="h-4 w-4" strokeWidth={1.5} /> Chat on WhatsApp
                        </a>
                        <div className="mt-5">
                            <button
                                onClick={onClose}
                                className="text-xs uppercase tracking-label text-zinc-600 hover:text-zinc-950 underline underline-offset-4"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="p-5 space-y-3">
                        <p className="text-xs text-zinc-600 leading-relaxed">
                            FWG is your single point of contact. Share your details and we'll follow up to negotiate and finalize your agreement.
                        </p>
                        <div>
                            <label className="text-[10px] uppercase tracking-label text-zinc-500">Full name</label>
                            <input data-testid="inquiry-name" value={form.name} onChange={(e) => update("name", e.target.value)}
                                className="mt-1 w-full border border-zinc-300 px-3 py-2.5 text-sm focus:border-zinc-950 focus:outline-none" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-label text-zinc-500">Email</label>
                            <input data-testid="inquiry-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
                                className="mt-1 w-full border border-zinc-300 px-3 py-2.5 text-sm focus:border-zinc-950 focus:outline-none" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-label text-zinc-500">Phone</label>
                            <input data-testid="inquiry-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)}
                                placeholder="+63 9xx xxx xxxx"
                                className="mt-1 w-full border border-zinc-300 px-3 py-2.5 text-sm focus:border-zinc-950 focus:outline-none" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-label text-zinc-500">Message</label>
                            <textarea data-testid="inquiry-message" value={form.message} onChange={(e) => update("message", e.target.value)}
                                rows={4}
                                placeholder="What kind of business will you register? Preferred move-in date?"
                                className="mt-1 w-full border border-zinc-300 px-3 py-2.5 text-sm focus:border-zinc-950 focus:outline-none resize-none" />
                        </div>

                        <button
                            data-testid="inquiry-submit"
                            onClick={submit}
                            disabled={busy}
                            className="mt-2 w-full bg-zinc-950 text-white py-3 text-sm font-medium hover:bg-[#FF6A13] transition-colors disabled:opacity-60"
                        >
                            {busy ? "Sending…" : "Send inquiry to FWG"}
                        </button>

                        <div className="pt-3 border-t border-zinc-200 mt-2">
                            <div className="text-[10px] uppercase tracking-label text-zinc-500 mb-2">Or reach us directly</div>
                            <a
                                href={FWG_WHATSAPP_LINK}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="inquiry-whatsapp-link"
                                className="flex items-center gap-2 text-sm text-zinc-800 hover:text-[#25D366]"
                            >
                                <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
                                WhatsApp · <span className="font-semibold">{FWG_WHATSAPP_DISPLAY}</span>
                            </a>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function CenterDetail() {
    const { id } = useParams();
    const [center, setCenter] = useState(null);
    const [active, setActive] = useState(0);
    const [inquiryOpen, setInquiryOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        api.get(`/centers/${id}`).then((r) => setCenter(r.data)).catch(() => setCenter(null));
    }, [id]);

    if (!center) {
        return <div className="pt-32 text-center text-zinc-500 text-sm tracking-label uppercase">Loading…</div>;
    }

    const photos = center.photos && center.photos.length > 0 ? center.photos : [
        "https://images.pexels.com/photos/7651627/pexels-photo-7651627.jpeg",
    ];
    const monthly = center.monthly_rate_php || 0;
    const annualCash = monthly * 11;
    const annualPdc = monthly * 12;
    const showContact = !!center.show_public_contact && (center.contact_email || center.contact_phone);

    return (
        <div className="pt-20">
            <Toaster richColors position="top-center" />
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-6">
                <Link to="/search" data-testid="back-link" className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-950">
                    <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to search
                </Link>
            </div>

            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-8">
                    <div className="text-[10px] uppercase tracking-label text-zinc-500 flex items-center gap-2">
                        <MapPin className="h-3 w-3" strokeWidth={1.5} /> {center.city} · {center.public_area}
                    </div>
                    <h1 data-testid="center-name" className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-none mt-3">
                        {center.name}
                    </h1>

                    <div className="mt-8">
                        <div className="aspect-[16/10] bg-zinc-100 overflow-hidden">
                            <img data-testid="gallery-main" src={photos[active]} alt="" className="h-full w-full object-cover" />
                        </div>
                        {photos.length > 1 && (
                            <div className="mt-3 grid grid-cols-6 gap-2">
                                {photos.map((p, i) => (
                                    <button
                                        key={i}
                                        data-testid={`gallery-thumb-${i}`}
                                        onClick={() => setActive(i)}
                                        className={`aspect-square overflow-hidden border-2 ${active === i ? "border-zinc-950" : "border-transparent"}`}
                                    >
                                        <img src={p} alt="" className="h-full w-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="mt-10">
                        <div className="text-[10px] uppercase tracking-label text-zinc-500">The address</div>
                        <p className="mt-3 text-zinc-700 leading-relaxed max-w-3xl">{center.description}</p>
                    </div>

                    {center.amenities?.length > 0 && (
                        <div className="mt-10">
                            <div className="text-[10px] uppercase tracking-label text-zinc-500">What's included</div>
                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {center.amenities.map((a) => (
                                    <div key={a} className="flex items-center gap-2 text-sm text-zinc-800 border border-zinc-200 px-3 py-2">
                                        <Check className="h-4 w-4 text-[#FF6A13]" strokeWidth={1.5} /> {a}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <aside className="lg:col-span-4">
                    <div className="lg:sticky lg:top-24 border border-zinc-950 bg-white">
                        <div className="bg-zinc-950 text-white p-5">
                            <div className="text-[10px] uppercase tracking-label text-white/60">Rent this address</div>
                            <div className="font-display text-2xl font-semibold mt-1">{center.name}</div>
                        </div>
                        <div className="p-5">
                            <div className="text-[10px] uppercase tracking-label text-zinc-500">Monthly rate</div>
                            <div data-testid="center-rate" className="font-display text-5xl font-semibold tracking-tighter mt-1">
                                {phpFmt(monthly)}
                            </div>
                            <div className="text-xs text-zinc-500 mt-1">per month · 12-month minimum term</div>

                            <div className="mt-6 space-y-3">
                                <div className="border border-zinc-200 p-4">
                                    <div className="text-[10px] uppercase tracking-label text-zinc-500">Option A · Post-Dated Checks</div>
                                    <div className="font-display text-lg font-semibold mt-1">12 PDCs × {phpFmt(monthly)}</div>
                                    <div className="text-xs text-zinc-600 mt-0.5">Total {phpFmt(annualPdc)} · 12 months</div>
                                </div>
                                <div className="border-2 border-[#FF6A13] p-4 bg-orange-50/40">
                                    <div className="flex items-center justify-between">
                                        <div className="text-[10px] uppercase tracking-label text-[#FF6A13] font-bold">Option B · Cash</div>
                                        <span className="text-[9px] uppercase tracking-label bg-[#FF6A13] text-white px-1.5 py-0.5">Save 1 mo</span>
                                    </div>
                                    <div className="font-display text-lg font-semibold mt-1">Pay {phpFmt(annualCash)} up-front</div>
                                    <div className="text-xs text-zinc-600 mt-0.5">11 months cash · get 12 months coverage</div>
                                </div>
                            </div>

                            <button
                                data-testid="rent-btn"
                                onClick={() => navigate(`/agreement/${center.center_id}`)}
                                className="mt-6 w-full bg-zinc-950 text-white py-4 font-medium hover:bg-[#FF6A13] transition-colors"
                            >
                                Rent this address
                            </button>

                            <button
                                data-testid="inquire-fwg-btn"
                                onClick={() => setInquiryOpen(true)}
                                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 bg-white border border-zinc-950 py-3 text-sm font-medium text-zinc-950 hover:bg-zinc-950 hover:text-white transition-colors"
                            >
                                <MessageCircle className="h-4 w-4" strokeWidth={1.5} /> Inquire via FWG
                            </button>

                            <Link
                                data-testid="preview-contract-link"
                                to={`/centers/${center.center_id}/contract-preview`}
                                className="mt-3 w-full inline-flex items-center justify-center border border-zinc-300 bg-white py-3 text-xs uppercase tracking-label text-zinc-800 hover:border-zinc-950 transition-colors"
                            >
                                Preview sample lease contract
                            </Link>

                            {showContact ? (
                                <div data-testid="public-contact-block" className="mt-5 border-t border-zinc-200 pt-4 space-y-1.5 text-xs text-zinc-700">
                                    <div className="text-[10px] uppercase tracking-label text-zinc-500 mb-2">Direct contact · FWG operations</div>
                                    {center.contact_name && (
                                        <div className="flex items-center gap-2"><User2 className="h-3.5 w-3.5" strokeWidth={1.5} /> {center.contact_name}</div>
                                    )}
                                    {center.contact_email && (
                                        <a href={`mailto:${center.contact_email}`} className="flex items-center gap-2 hover:text-[#FF6A13]">
                                            <Mail className="h-3.5 w-3.5" strokeWidth={1.5} /> {center.contact_email}
                                        </a>
                                    )}
                                    {center.contact_phone && (
                                        <a href={`tel:${center.contact_phone.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-[#FF6A13]">
                                            <Phone className="h-3.5 w-3.5" strokeWidth={1.5} /> {center.contact_phone}
                                        </a>
                                    )}
                                </div>
                            ) : (
                                <div className="mt-4 flex items-center gap-2 text-[11px] text-zinc-500">
                                    <Shield className="h-3.5 w-3.5" strokeWidth={1.5} />
                                    Your single point of contact is FWG — always.
                                </div>
                            )}
                        </div>
                    </div>
                </aside>
            </div>

            <InquiryModal
                open={inquiryOpen}
                onClose={() => setInquiryOpen(false)}
                centerId={center.center_id}
                centerName={center.name}
            />
        </div>
    );
}
